import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, ShieldCheck, ShieldAlert, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS = {
  approved: { icon: ShieldCheck, color: "#2e7d32", label: "Age verified (21+)" },
  rejected: { icon: ShieldAlert, color: "#d32f2f", label: "Verification failed" },
  review: { icon: Clock, color: "#b5610a", label: "Pending admin review" },
  pending: { icon: Clock, color: "#b5610a", label: "Pending review" },
};

export default function Account() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    api.get("/orders").then(({ data }) => setOrders(data)).catch(() => {});
  }, []);

  if (!user) return null;
  const st = STATUS[user.verification_status] || STATUS.pending;
  const StIcon = st.icon;

  return (
    <div className="mx-auto max-w-5xl px-6 py-16">
      <p className="overline mb-2">My Account</p>
      <h1 className="mb-10 text-4xl font-black text-[#2b2520]">Hello, {user.name}</h1>

      <div className="mb-12 grid gap-6 sm:grid-cols-2">
        <div className="border border-black/10 bg-white p-6">
          <p className="overline mb-3">Account</p>
          <p className="text-[#2b2520]">{user.email}</p>
          <p className="mt-1 text-sm text-neutral-500">DOB: {user.dob}</p>
        </div>
        <div className="border border-black/10 bg-white p-6" data-testid="verification-status">
          <p className="overline mb-3">Verification</p>
          <div className="flex items-center gap-2" style={{ color: st.color }}>
            <StIcon className="h-5 w-5" strokeWidth={1.5} />
            <span className="font-medium">{st.label}</span>
          </div>
          {!user.age_verified && (
            <p className="mt-2 text-xs text-neutral-500">
              You can browse, but checkout unlocks once your ID is approved.
            </p>
          )}
        </div>
      </div>

      <div className="mb-6 flex items-center gap-2">
        <Package className="h-5 w-5 text-[#a06d34]" strokeWidth={1.5} />
        <h2 className="text-xl font-bold text-[#2b2520]">Order History</h2>
      </div>

      {orders.length === 0 ? (
        <div className="border border-black/10 bg-white p-10 text-center text-neutral-500">
          No orders yet.{" "}
          <Link to="/" className="text-[#a06d34] hover:underline">
            Start shopping
          </Link>
        </div>
      ) : (
        <div className="space-y-4" data-testid="orders-list">
          {orders.map((o) => (
            <div key={o.order_id} className="border border-black/10 bg-white p-6">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <span className="font-mono text-xs text-neutral-500">
                  #{o.order_id.slice(0, 8)}
                </span>
                <span
                  className="border px-3 py-1 text-[10px] uppercase tracking-widest"
                  style={{
                    borderColor: o.payment_status === "paid" ? "#2e7d3266" : "#b5610a66",
                    color: o.payment_status === "paid" ? "#2e7d32" : "#b5610a",
                  }}
                >
                  {o.status}
                </span>
              </div>
              <div className="space-y-1 text-sm text-neutral-600">
                {o.items.map((it, i) => (
                  <div key={i} className="flex justify-between">
                    <span>
                      {it.name} × {it.quantity}
                    </span>
                    <span className="text-[#2b2520]">${(it.price * it.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex justify-between border-t border-black/10 pt-4 font-semibold text-[#2b2520]">
                <span>Total</span>
                <span>${o.total.toFixed(2)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
